/* global SWDLocalise */
import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import history from 'instantsearch.js/es/lib/routers/history';
import { connectMenu } from 'instantsearch.js/es/connectors';
import { configure, stats, pagination } from 'instantsearch.js/es/widgets';
import customHits from './widgets/hits-recipe';
import customMenuSelect from './widgets/menu-select';
import customMenuSelectProduct from './widgets/menu-select-product';
import customCurrentRefinements from './widgets/current-refinements';
import contentSelect from '../dial/select-content';
import animations from '../animations';
import { handleResponse, capFirstLetter } from '../../utils';

const DOM = {
    searchResults: document.querySelector('.js-recipe-search-results'),
};

const updateContent = (category) => {
    if (category === 'all') {
        const title = document.querySelector('.js-recipe-search-title');
        title.innerHTML = title.dataset.allContent;
        document.querySelector('.js-recipe-search-subtitle').innerHTML = 'Recipe Results';
        document.querySelector('.js-recipe-search-components').innerHTML = '';
        return;
    }

    const formattedCategory = category.split('-').join(' ').replace('and', '&amp;');
    document.querySelector('.js-recipe-search-title').innerHTML = `<h1>${formattedCategory} Recipes</h1>`;
    document.querySelector('.js-recipe-search-subtitle').innerHTML = `${formattedCategory} Recipe Results`;

    // get page components
    const container = document.querySelector('.js-recipe-search-components');
    container.innerHTML = '<div class="o-mb60 u-center"><svg class="c-icon c-loader-icon"><use xlink:href="#icon-loader"></use></svg></div>';

    const data = new FormData();
    data.append('action', 'swd_get_page_components');
    data.append('nonce', container.dataset.nonce);
    data.append('category', category);
    data.append('tax', 'recipe_cat');

    fetch(SWDLocalise.ajaxUrl, {
        method: 'POST',
        cache: 'no-cache',
        body: data,
    })
        .then(handleResponse)
        .then((response) => {
            if (response.success) {
                container.innerHTML = response.data.components;
                animations();
            }
        })
        .catch((error) => {
            console.error(error); // eslint-disable-line no-console
        });
};

const setup = () => {
    const algoliaClient = algoliasearch(
        SWDLocalise.algoliaAppID,
        SWDLocalise.algoliaAPIKey,
    );

    const searchClient = {
        ...algoliaClient,
    };

    const indexName = `${SWDLocalise.algoliaIndex}recipe`;

    const search = instantsearch({
        searchClient,
        indexName,
        routing: {
            router: history({
                createURL({ qsModule, routeState, location }) {
                    const urlParts = location.href.match(/^(.*?)\/recipes/);
                    const baseUrl = `${urlParts ? urlParts[1] : ''}/`;

                    const categoryPath = routeState.category
                        ? `${routeState.category}/`
                        : '';

                    const queryParameters = {};

                    // page
                    if (routeState.batch !== 1) {
                        queryParameters.batch = routeState.batch;
                    }

                    // occasion
                    if (routeState.occasion) {
                        queryParameters.occasion = routeState.occasion;
                    }

                    // product
                    if (routeState.products) {
                        queryParameters.products = routeState.products;
                    }

                    const queryString = qsModule.stringify(queryParameters, {
                        addQueryPrefix: true,
                        arrayFormat: 'repeat',
                    });

                    // handle single URL
                    return `${baseUrl}recipes/${categoryPath}${queryString}`;
                },

                parseURL({ qsModule, location }) {
                    const pathnameMatches = location.pathname.match(/recipes\/(.*?)\/?$/);
                    const category = ((pathnameMatches && pathnameMatches[1]) || '');
                    const {
                        batch,
                        occasion = '',
                        products = '',
                    } = qsModule.parse(
                        location.search.slice(1),
                    );

                    return {
                        batch,
                        category,
                        occasion,
                        products,
                    };
                },
            }),

            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState.wp_spb_recipe || {};
                    return {
                        batch: indexUiState.page,
                        category: indexUiState.menu && indexUiState.menu['taxonomies.recipe_cat'],
                        occasion: indexUiState.menu && indexUiState.menu['taxonomies.occasion'],
                        products: indexUiState.menu && indexUiState.menu.product,
                    };
                },

                routeToState(routeState) {
                    return {
                        wp_spb_recipe: {
                            page: routeState.batch,
                            menu: {
                                'taxonomies.recipe_cat': routeState.category,
                                'taxonomies.occasion': routeState.occasion,
                                product: routeState.products,
                            },
                        },
                    };
                },
            },
        },
        searchFunction(helper) {
            const page = helper.getPage() || 0;

            helper
                .toggleFacetRefinement('visibility', 'visible')
                .setPage(page);

            if (helper.hasRefinements('taxonomies.recipe_cat')) {
                updateContent(helper.state.hierarchicalFacetsRefinements['taxonomies.recipe_cat'][0]);
                contentSelect(helper.state.hierarchicalFacetsRefinements['taxonomies.recipe_cat'][0]);
            } else {
                updateContent('all');
                contentSelect('all');
            }

            helper.search();
        },
    });

    const virtualMenu = connectMenu(() => null);

    search.addWidgets([
        configure({
            hierarchicalFacets: [{
                name: 'categories',
                attributes: ['taxonomies.recipe_cat.lvl0'],
            }],
        }),

        virtualMenu({
            attribute: 'visibility',
        }),

        customCurrentRefinements({
            container: document.querySelector('.js-recipe-search-current'),
            transformItems(items) {
                return items.map((item) => ({
                    ...item,
                    label: item.label
                        .replace('taxonomies.recipe_cat', 'Category')
                        .replace('taxonomies.occasion', 'Occasion')
                        .replace('_', ' '),
                }));
            },
            excludedAttributes: ['visibility'],
        }),

        customMenuSelect({
            container: document.querySelector('.js-recipe-search-cats'),
            attribute: 'taxonomies.recipe_cat',
            sortBy: ['name:asc'],
            transformItems(items) {
                return items.map((item) => ({
                    ...item,
                    label: item.label.split('-')
                        .map(decodeURIComponent)
                        .map(capFirstLetter)
                        .join(' '),
                }));
            },
            limit: 30,
        }),

        customMenuSelect({
            container: document.querySelector('.js-recipe-search-occasions'),
            attribute: 'taxonomies.occasion',
            sortBy: ['name:asc'],
            transformItems(items) {
                return items.map((item) => ({
                    ...item,
                    label: item.label.split('-')
                        .map(decodeURIComponent)
                        .map(capFirstLetter)
                        .join(' '),
                }));
            },
            limit: 30,
        }),

        customMenuSelectProduct({
            container: document.querySelector('.js-recipe-search-products'),
            attribute: 'product',
            limit: 30,
        }),

        stats({
            container: document.querySelector('.js-recipe-search-stats'),
            templates: {
                text: `
                    {{^areHitsSorted}}
                        {{#hasNoResults}}No Recipes{{/hasNoResults}}
                        {{#hasOneResult}}Found 1 Recipe{{/hasOneResult}}
                        {{#hasManyResults}}Found {{#helpers.formatNumber}}{{nbHits}}{{/helpers.formatNumber}} Recipes{{/hasManyResults}}
                    {{/areHitsSorted}}
                `,
            },
        }),

        stats({
            container: document.querySelector('.js-recipe-filter-stats'),
            templates: {
                text: `
                    {{^areHitsSorted}}
                        {{#hasNoResults}}View Recipes (0){{/hasNoResults}}
                        {{#hasOneResult}}View Recipes (1){{/hasOneResult}}
                        {{#hasManyResults}}View Recipes ({{#helpers.formatNumber}}{{nbHits}}{{/helpers.formatNumber}}){{/hasManyResults}}
                    {{/areHitsSorted}}
                `,
            },
        }),

        customHits({
            container: DOM.searchResults,
        }),

        pagination({
            container: document.querySelector('.js-recipe-search-pagination'),
            scrollTo: document.querySelector('.js-dial-scroll-target'),
        }),
    ]);

    search.start();

    // refine recipes
    const refineRecipes = (category) => {
        if (category === 'all') {
            search.helper
                .clearRefinements('taxonomies.recipe_cat')
                .clearRefinements('taxonomies.occasion')
                .clearRefinements('product')
                .search();
            return;
        }

        search.helper
            .clearRefinements('taxonomies.recipe_cat')
            .toggleFacetRefinement('taxonomies.recipe_cat', category)
            .search();
    };

    // handle dial recipe category button filtering
    const categoryBtns = document.querySelectorAll('.js-recipe-search-cat-btn');
    categoryBtns.forEach((categoryBtn) => {
        categoryBtn.addEventListener('click', () => {
            const { category } = categoryBtn.dataset;
            // call Algolia
            refineRecipes(category);
        });
    });

    // handle dial recipe select filtering
    const categorySelect = document.querySelector('.js-recipe-search-select');
    categorySelect.addEventListener('change', (event) => {
        event.preventDefault();
        // call Algolia
        refineRecipes(categorySelect.value);
    });
};

const init = () => {
    if (!DOM.searchResults) return;
    setup();
};

export default init;
